import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import PaidRoundedIcon from '@mui/icons-material/PaidRounded'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { displayNamesOfEventTypes } from 'src/consumer/constants'
import {
  markSupportBookedEventAsPaid,
  uploadSupportFeeProof,
} from 'src/supportPortal/services/supportBookedEventActionsService'

dayjs.extend(utc)

// Collected-by is the settlement-critical choice: it drives the booking's feePaymentMode and
// therefore the payout / clinic settlement split. The specific mode within each party is for
// record-keeping (and, for clinic collections, becomes the feePaymentMode slug on the server).
const CLINIC_MODE_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'phonepe', label: 'UPI — PhonePe' },
  { value: 'google_pay', label: 'UPI — Google Pay' },
  { value: 'paytm', label: 'UPI — Paytm' },
  { value: 'other_upi', label: 'UPI — Other' },
  { value: 'debit_and_credit_cards', label: 'Card (POS machine)' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Other' },
]
const AUMHUM_MODE_OPTIONS = [
  { value: 'aumhum_upi', label: 'UPI to AUMHUM' },
  { value: 'aumhum_bank_transfer', label: 'Bank transfer to AUMHUM' },
  { value: 'razorpay', label: 'Online payment link / Razorpay' },
  { value: 'aumhum_card', label: 'Card (AUMHUM)' },
  { value: 'aumhum_other', label: 'Other' },
]

const ACCEPTED_TYPES = 'image/*,application/pdf'
const MAX_FILE_BYTES = 15 * 1024 * 1024

function isImageMime(mimeType) {
  return typeof mimeType === 'string' && mimeType.startsWith('image/')
}

export default function SupportMarkAsPaidDialog({ open, onClose, bookedEvent, onSuccess }) {
  const [collectedByParty, setCollectedByParty] = useState('clinic')
  const [paymentGatewayUsed, setPaymentGatewayUsed] = useState(CLINIC_MODE_OPTIONS[0].value)
  const [reasonGiven, setReasonGiven] = useState('')
  const [paymentTxnId, setPaymentTxnId] = useState('')
  const [feeCollectionNote, setFeeCollectionNote] = useState('')
  const [proofDocuments, setProofDocuments] = useState([])
  const [uploadingCount, setUploadingCount] = useState(0)
  const [saving, setSaving] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const fileInputRef = useRef(null)

  useEffect(() => {
    if (!open) {
      return
    }
    setCollectedByParty('clinic')
    setPaymentGatewayUsed(CLINIC_MODE_OPTIONS[0].value)
    setReasonGiven('')
    setPaymentTxnId('')
    setFeeCollectionNote('')
    setProofDocuments([])
    setUploadingCount(0)
    setSubmitError('')
  }, [open, bookedEvent])

  const isAumhum = collectedByParty === 'aumhum'
  const modeOptions = isAumhum ? AUMHUM_MODE_OPTIONS : CLINIC_MODE_OPTIONS

  const patientName = bookedEvent?.patientInfoData?.name || bookedEvent?.userName || 'Patient'
  const eventTypeLabel =
    displayNamesOfEventTypes?.[bookedEvent?.eventType] || bookedEvent?.eventType || 'Session'
  const feeLabel = bookedEvent?.feePaid
    ? `${bookedEvent?.currency || 'INR'} ${bookedEvent.feePaid}`
    : null

  function handleCollectedByChange(nextParty) {
    if (!nextParty || nextParty === collectedByParty) {
      return
    }
    setCollectedByParty(nextParty)
    setPaymentGatewayUsed(
      (nextParty === 'aumhum' ? AUMHUM_MODE_OPTIONS : CLINIC_MODE_OPTIONS)[0].value,
    )
  }

  async function handleFilesSelected(event) {
    const files = Array.from(event.target.files || [])
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    for (const file of files) {
      if (file.size > MAX_FILE_BYTES) {
        toast.error(`${file.name} is larger than 15 MB`)
        continue
      }
      setUploadingCount((count) => count + 1)
      const result = await uploadSupportFeeProof(file)
      setUploadingCount((count) => Math.max(0, count - 1))
      if (result.ok && result.proofDocument?.fileId) {
        setProofDocuments((docs) => [...docs, result.proofDocument])
      } else {
        toast.error(result.message || `Could not upload ${file.name}`)
      }
    }
  }

  function removeDocument(fileId) {
    setProofDocuments((docs) => docs.filter((doc) => doc.fileId !== fileId))
  }

  async function handleSubmit() {
    if (!reasonGiven.trim()) {
      toast.error('Please describe how/why this payment is confirmed')
      return
    }
    setSaving(true)
    setSubmitError('')
    const result = await markSupportBookedEventAsPaid({
      bookedEventId: bookedEvent?.id,
      reasonGiven: reasonGiven.trim(),
      collectedByParty,
      paymentGatewayUsed,
      paymentTxnId: paymentTxnId.trim() || undefined,
      feeCollectionNote: feeCollectionNote.trim() || undefined,
      proofDocuments: proofDocuments.length ? proofDocuments : undefined,
    })
    setSaving(false)

    if (result.ok) {
      toast.success('Booking marked as paid')
      onSuccess?.()
      onClose?.()
      return
    }
    setSubmitError(result.message)
    toast.error(result.message)
  }

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '14px' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <PaidRoundedIcon sx={{ color: '#047857' }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 17, color: '#0f172a' }}>
              Mark as Paid
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>
              {patientName} · {eventTypeLabel}
              {bookedEvent?.startTime
                ? ` · ${dayjs.utc(bookedEvent.startTime).local().format('DD MMM, h:mm A')}`
                : ''}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {submitError ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {submitError}
            </Alert>
          ) : null}

          {/* Collected by — the settlement-critical choice */}
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: '#64748b',
                mb: 0.75,
              }}
            >
              Collected by
            </Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              value={collectedByParty}
              onChange={(event, nextValue) => handleCollectedByChange(nextValue)}
              sx={{
                height: 46,
                p: '4px',
                gap: '4px',
                backgroundColor: '#f1f5f9',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                '& .MuiToggleButtonGroup-grouped': { m: 0, border: 0, borderRadius: '9px !important' },
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: '#64748b',
                  '&.Mui-selected': {
                    backgroundColor: '#ffffff',
                    color: '#0f172a',
                    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.12)',
                    '&:hover': { backgroundColor: '#ffffff' },
                  },
                },
              }}
            >
              <ToggleButton value="clinic">Clinic</ToggleButton>
              <ToggleButton value="aumhum">AUMHUM</ToggleButton>
            </ToggleButtonGroup>
            <Alert
              severity={isAumhum ? 'info' : 'warning'}
              sx={{ mt: 1, borderRadius: 2, fontSize: 12.5, py: 0.25 }}
            >
              {isAumhum
                ? 'AUMHUM received this fee — it settles as AUMHUM-collected in the payout report.'
                : 'The clinic received this fee — it settles as clinic-collected in the payout report.'}
            </Alert>
          </Box>

          <TextField
            select
            label="Payment mode"
            size="small"
            fullWidth
            value={paymentGatewayUsed}
            onChange={(event) => setPaymentGatewayUsed(event.target.value)}
          >
            {modeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Reason / how the payment was received *"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={reasonGiven}
            onChange={(event) => setReasonGiven(event.target.value)}
            placeholder={
              isAumhum
                ? 'e.g. Patient paid into the AUMHUM UPI/bank account'
                : 'e.g. Patient paid in cash at the front desk'
            }
          />

          <TextField
            label="Transaction reference (optional)"
            size="small"
            fullWidth
            value={paymentTxnId}
            onChange={(event) => setPaymentTxnId(event.target.value)}
          />

          {/* Proof documents */}
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#64748b',
                }}
              >
                Proof documents
              </Typography>
              <Button
                size="small"
                startIcon={
                  uploadingCount > 0 ? (
                    <CircularProgress size={13} />
                  ) : (
                    <CloudUploadOutlinedIcon sx={{ fontSize: 16 }} />
                  )
                }
                onClick={() => fileInputRef.current?.click()}
                disabled={saving}
                sx={{
                  textTransform: 'none',
                  fontSize: 12.5,
                  fontWeight: 650,
                  borderRadius: '8px',
                  color: '#4338ca',
                }}
              >
                {uploadingCount > 0 ? 'Uploading…' : 'Upload'}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES}
                multiple
                hidden
                onChange={handleFilesSelected}
              />
            </Stack>
            {proofDocuments.length === 0 ? (
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: '1px dashed rgba(148, 163, 184, 0.6)',
                  borderRadius: '10px',
                  py: 1.5,
                  textAlign: 'center',
                  cursor: 'pointer',
                  color: '#94a3b8',
                  fontSize: 12.5,
                  '&:hover': { borderColor: 'rgba(79, 70, 229, 0.5)', backgroundColor: '#fafbff' },
                }}
              >
                Attach payment screenshots or receipts (image / PDF, up to 15 MB)
              </Box>
            ) : (
              <Stack spacing={0.75}>
                {proofDocuments.map((doc) => (
                  <Chip
                    key={doc.fileId}
                    icon={
                      isImageMime(doc.mimeType) ? (
                        <ImageOutlinedIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <InsertDriveFileOutlinedIcon sx={{ fontSize: 16 }} />
                      )
                    }
                    label={doc.fileName || 'Document'}
                    onDelete={saving ? undefined : () => removeDocument(doc.fileId)}
                    deleteIcon={<CloseRoundedIcon />}
                    variant="outlined"
                    sx={{
                      justifyContent: 'flex-start',
                      maxWidth: '100%',
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  />
                ))}
              </Stack>
            )}
          </Box>

          <TextField
            label="Fee collection note (optional)"
            size="small"
            fullWidth
            value={feeCollectionNote}
            onChange={(event) => setFeeCollectionNote(event.target.value)}
          />

          <Alert severity="warning" sx={{ borderRadius: 2, fontSize: 12.5, py: 0.25 }}>
            This confirms {feeLabel ? <strong>{feeLabel}</strong> : 'the fee'} was collected outside
            the payment gateway and books the session. The confirmation is recorded against your
            account.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={saving || uploadingCount > 0 || !reasonGiven.trim()}
        >
          {saving ? 'Confirming...' : 'Confirm Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
